import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { API_TAGS } from '@/shared/api';
import { fetchThemeConfig, LOCAL_PROJECT_CONFIG, updateGeneralSettings } from './api';
import type {
  InterruptionSettings,
  ProjectConfigResponse,
  TimeDisplayComponent,
  TimeDisplayFormat,
  TimeDisplaySettings,
} from './types';

/** Query-ключи для запросов настроек проекта */
export const settingsKeys = {
  /** Корневой ключ всех запросов настроек */
  all: [API_TAGS.SETTINGS] as const,
  /** Ключ запроса конфигурации тем проекта */
  themeConfig: () => [...settingsKeys.all, 'themeConfig'] as const,
};

/**
 * Хук для получения конфигурации проекта (темы в формате ProjectConfigResponse)
 *
 * @returns Query объект с данными (themes — массив, colorParameter с code в snake_case)
 */
export const useThemeConfig = () => {
  return useQuery<ProjectConfigResponse>({
    queryKey: settingsKeys.themeConfig(),
    queryFn: fetchThemeConfig,
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

/** Настройка конфигурации проекта, в которой хранится формат интервалов компонента */
const COMPONENT_FORMAT_FIELD: Record<TimeDisplayComponent, keyof TimeDisplaySettings> = {
  player: 'playerTimeDisplayFormat',
  transcript: 'transcriptTimeDisplayFormat',
  expertReport: 'expertReportTimeDisplayFormat',
  filter: 'filterTimeDisplayFormat',
};

/**
 * Действующий формат отображения интервалов времени: переданного компонента, а без аргумента —
 * остальных интервалов, у которых своего компонента нет.
 * Пока конфигурация проекта не загружена (и при недоступности бэкенда) — значение локального конфига.
 */
export const useTimeDisplayFormat = (component?: TimeDisplayComponent): TimeDisplayFormat => {
  const { data } = useThemeConfig();
  const config = data ?? LOCAL_PROJECT_CONFIG;
  return config[component ? COMPONENT_FORMAT_FIELD[component] : 'timeDisplayFormat'];
};

/**
 * Действующие пороги распознавания перебиваний, вставок и пауз.
 * Пока конфигурация проекта не загружена (и при недоступности бэкенда) — значения локального конфига.
 */
export const useInterruptionSettings = (): InterruptionSettings => {
  const { data } = useThemeConfig();
  const {
    interruptionMinDurationMs,
    interruptionMaxDurationMs,
    pauseMinDurationMs,
    insertMinDurationMs,
    insertSeriesMinCount,
    insertMaxGapMs,
  } = data ?? LOCAL_PROJECT_CONFIG;

  return useMemo(
    () => ({
      interruptionMinDurationMs,
      interruptionMaxDurationMs,
      pauseMinDurationMs,
      insertMinDurationMs,
      insertSeriesMinCount,
      insertMaxGapMs,
    }),
    [
      interruptionMinDurationMs,
      interruptionMaxDurationMs,
      pauseMinDurationMs,
      insertMinDurationMs,
      insertSeriesMinCount,
      insertMaxGapMs,
    ],
  );
};

/**
 * Изменение общих настроек системы: по успеху конфигурация проекта перезапрашивается,
 * и новые значения начинают действовать во всех интерфейсах.
 */
export const useUpdateGeneralSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateGeneralSettings,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settingsKeys.themeConfig() }),
  });
};
